import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BreadCrumb } from '@nationwide/internet-servicing-angular-components';
import { PolicyInfoService } from 'app/commercial/claims/services/policy-info.service';
import { environment } from 'environments/environment';
import { Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { commercialConstants } from '../../../../common/commercial-constants';
import { LoggerService } from '../../../../shared/services/logger.service';
import { ClaimsDataService } from '../../services/claims-data.service';
import { CommercialLoggingService } from '../../services/commercial-logging.service';
import { GetClaimsService } from '../../services/get-claims.service';
import { IconSvgService } from '../../services/icon-svg.service';

@Component({
    selector: 'is-claim-overview',
    templateUrl: './claim-overview.component.html',
    styleUrls: ['./claim-overview.component.scss'],
    standalone: false
})
export class ClaimOverviewComponent implements OnInit, OnDestroy {
    allEvents: any[] = [];
    breadCrumbs: BreadCrumb[];
    claimEventsFiltered: any;
    claimInfo: any;
    claimReceivedOrClosed = false;
    contactsOnly$ = new Subject<string>();
    count = 0;
    currentPage = 'Claim Overview';
    homeIconLink = environment.NATIONWIDE.accountSummaryPage;
    isAuto: boolean;
    lossCause: string;
    offset = 0;
    paymentIssued = false;
    paymentsOnly$ = new Subject<string>();
    serviceFlag: boolean;
    subscription = new Subscription();
    totalCount = 0;

    constructor(
        private getClaimsService: GetClaimsService,
        private LOGGER: LoggerService,
        private router: Router,
        public claimsDataService: ClaimsDataService,
        public policyInfoService: PolicyInfoService,
        private iconSvgService: IconSvgService,
        private commercialLoggingService: CommercialLoggingService
    ) { }
    checkFlow(): void {
        if (this.claimReceivedOrClosed === true) {
            this.navigateToIncidentDetails();
        } else {
            this.navigateToPayments();
        }
    }
    getClaimEvents(): void {
        const offsetIncrement = 30;
        const claim = this.claimsDataService.getClaimNumber();
        // eslint-disable-next-line sonarjs/cognitive-complexity
        this.subscription = this.getClaimsService.getClaimEvents(claim, `?limit=30&offset=${this.offset}`).subscribe((response) => {
            this.allEvents = this.allEvents.concat(response.body.claimEvents);
            this.totalCount = response.headers.get('X-Total-Count');
            if (this.allEvents.length > 0) {
                this.offset += offsetIncrement;
                if (this.totalCount > this.allEvents.length) {
                    this.getClaimEvents();
                } else {
                    this.claimsDataService.setClaimEvents(this.allEvents);
                    for (const iterator of this.allEvents) {
                        if (iterator.eventType === 'claimReceived' || iterator.eventType === 'claimClosed') {
                            this.count = this.count + 1;
                            this.claimReceivedOrClosed = true;
                            this.claimEventsFiltered = iterator;
                        } else if (iterator.eventType === 'paymentIssued') {
                            this.count = this.count + 1;
                            this.paymentIssued = true;
                            this.claimEventsFiltered = iterator;
                        }
                        if (this.count === 1) {
                            break;
                        }
                    }
                }
            }
            this.commercialLoggingService.logServiceSuccess(
                this.LOGGER,
                this.currentPage,
                'GetClaimsService',
                'getClaimEvents',
                this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber(), claimsDisplayCount: this.totalCount || '1' })
            );
        }, (error) => {
            this.commercialLoggingService.logServiceFailure(
                this.LOGGER,
                this.currentPage,
                'GetClaimsService',
                'getClaimEvents',
                this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber() }),
                error
            );
        });
    }
    getClaimInfo(): void {
        const claim = this.claimsDataService.getClaimNumber();
        this.getClaimsService.getClaims(claim).pipe(take(1)).subscribe((response) => {
            this.claimsDataService.setClaimInformation(response);
            this.serviceFlag = true;
            this.claimInfo = this.claimsDataService.getClaimInformation();
            if (response.incidentOnly === false && this.isAuto === true) {
                this.contactsOnly$.next('true');
            }
            if (response.incidentOnly === false && this.isAuto === true) {
                this.paymentsOnly$.next('true');
            } else if (response.incidentOnly === false && this.isAuto === false) {
                this.paymentsOnly$.next('true');
            }
            this.commercialLoggingService.logServiceSuccess(
                this.LOGGER,
                this.currentPage,
                'GetClaimsService',
                'getClaims',
                this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber(), claimsDisplayCount: '1' })
            );
            this.commercialLoggingService.logPageLoad(this.LOGGER, this.currentPage, this.getLogMetadata());
        }, (error) => {
            this.commercialLoggingService.logServiceFailure(
                this.LOGGER,
                this.currentPage,
                'GetClaimsService',
                'getClaims',
                this.getLogMetadata({ claimNumber: this.claimsDataService.getClaimNumber() }),
                error
            );
        });
    }
    navigateToAllUpdates(): void {
        this.commercialLoggingService.logButtonAction(this.LOGGER, this.currentPage, 'navigate-all-updates', 'success', this.getLogMetadata());
        this.commercialLoggingService.updatePreviousUrlContext();
        this.router.navigate([commercialConstants.URLS.allUpdates]);
    }
    navigateToClaimContacts(): void {
        this.commercialLoggingService.logButtonAction(this.LOGGER, this.currentPage, 'navigate-claim-contacts', 'success', this.getLogMetadata());
        this.commercialLoggingService.updatePreviousUrlContext();
        this.router.navigate([commercialConstants.URLS.claimContacts]);
    }
    navigateToIncidentDetails(): void {
        this.commercialLoggingService.logButtonAction(this.LOGGER, this.currentPage, 'navigate-incident-details', 'success', this.getLogMetadata());
        this.commercialLoggingService.updatePreviousUrlContext();
        this.router.navigate([commercialConstants.URLS.incidentDetails]);
    }
    navigateToPayments(): void {
        this.commercialLoggingService.logButtonAction(this.LOGGER, this.currentPage, 'navigate-payments', 'success', this.getLogMetadata());
        this.commercialLoggingService.updatePreviousUrlContext();
        this.router.navigate([commercialConstants.URLS.payments]);
    }
    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    ngOnInit(): void {
        this.breadCrumbs = [{
            name: 'Package (PAK) Claims',
            link: `${commercialConstants.URLS.pakClaimsList}`
        }];
        this.isAuto = this.claimsDataService.getClaimsPolicyType() === 'Business Auto';
        this.claimInfo = this.claimsDataService.getClaimInformation();
        this.getClaimInfo();
        this.iconSvgService.init();
        this.getClaimEvents();
        this.commercialLoggingService.updatePreviousUrlContext();
    }

    private getLogMetadata(additionalFields: any = {}): any {
        return {
            policyNumber: this.claimsDataService.getClaimsPolicyNumber(),
            policyType: this.claimsDataService.getClaimsPolicyType(),
            lineOfBusiness: this.claimsDataService.getFnolFlow(),
            claimsDisplayCount: this.claimInfo ? '1' : this.claimsDataService.getClaimsDisplayCount() || 'UNKNOWN',
            alphaState: this.claimsDataService.getPrimary(this.claimsDataService.getClaimsPackage()?.address)?.state || 'UNKNOWN',
            previousUrl: window.sessionStorage.getItem('previousUrl') || '',
            claimNumber: this.claimInfo?.claimNumber || '',
            claimStatus: this.claimInfo?.claimState || '',
            incidentDate: this.claimInfo?.lossDate || '',
            lossCause: this.claimInfo?.lossCause || '',
            ...additionalFields
        };
    }
}
